import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { CheckCircle, Clock, XCircle } from "@phosphor-icons/react";

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [txn, setTxn] = useState(null);

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!cancelled && !sessionId) setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTxn(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("expired"); return; }
      } catch { setState("error"); return; }
      if (++tries >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]);

  const view = {
    checking: { icon: Clock, cls: "text-muted-foreground", title: "Confirming your payment…", sub: "Checking with Stripe." },
    paid: { icon: CheckCircle, cls: "text-success", title: "Payment received", sub: "The invoice has been marked as paid." },
    pending: { icon: Clock, cls: "text-chart-4", title: "Payment still processing", sub: "Refresh this page in a moment." },
    expired: { icon: XCircle, cls: "text-destructive", title: "Checkout session expired", sub: "Generate a fresh payment link." },
    cancelled: { icon: XCircle, cls: "text-destructive", title: "Payment cancelled", sub: "No amount was charged." },
    error: { icon: XCircle, cls: "text-destructive", title: "Could not verify payment", sub: "Open the invoice and check the payment status." },
  }[state];
  const Icon = view.icon;

  return (
    <div className="flex min-h-screen items-center justify-center p-6" data-testid="payment-result-page">
      <div className="w-full max-w-md border border-border p-8">
        <Icon size={32} className={view.cls} />
        <h1 className="mt-4 font-display text-2xl font-bold" data-testid="payment-result-title">{view.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{view.sub}</p>
        {txn ? (
          <div className="mt-5 grid grid-cols-2 gap-px border border-border bg-border text-sm">
            <div className="bg-background p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Invoice</p>
              <p className="num">{txn.invoice_no}</p>
            </div>
            <div className="bg-background p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Amount</p>
              <p className="num" data-testid="payment-amount">{money(txn.amount)}</p>
            </div>
          </div>
        ) : null}
        <Button className="mt-6 w-full" onClick={() => navigate("/vouchers")} data-testid="back-to-daybook-btn">
          Back to day book
        </Button>
      </div>
    </div>
  );
}
